/**
 * ApproachingFutureFaresStrip · v2.7.65+E215
 *
 * A compact, additive indicator rendered at the TOP of the Ride Coordinator.
 * It surfaces Future Fares that are approaching within 1 hour of their pickup
 * (backend flag `ready_for_dispatch`) so dispatchers know an advance booking is
 * coming up. It does NOT change any existing Ride Coordinator behavior — it
 * only reads the isolated future_fares feed and links to the Future Fares view.
 */
import React, { useEffect, useState, useCallback } from "react";
import { CalendarClock, ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import adminApi from "@/lib/adminApi";

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function ApproachingFutureFaresStrip() {
  const [fares, setFares] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/future-fares", { params: { ready: true } });
      setFares((data && data.future_fares) || []);
    } catch {
      /* silent — strip just stays hidden */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (!loaded || fares.length === 0) return null;

  return (
    <div
      data-testid="coord-approaching-future-fares"
      className="rounded-lg border border-amber-400/40 bg-amber-400/5 p-3"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-amber-300">
          <span className="relative flex h-4 w-4 items-center justify-center">
            <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400/40 animate-ping" />
            <CalendarClock className="relative h-4 w-4" />
          </span>
          <span className="text-xs font-black uppercase tracking-wider">
            {fares.length} Future Fare{fares.length > 1 ? "s" : ""} approaching (within 1 hour)
          </span>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/future-fares")}
          data-testid="coord-approaching-view-all"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-200 hover:text-amber-100 transition-colors"
        >
          View Future Fares <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      <div className="flex flex-col gap-1.5">
        {fares.slice(0, 5).map((f) => (
          <div
            key={f.id}
            data-testid={`coord-approaching-${f.id}`}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-200 bg-zinc-950/50 rounded px-2 py-1.5"
          >
            <span className="font-mono text-[10px] text-amber-300">{f.id}</span>
            <span className="font-bold">{f.customer_name || "—"}</span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-300 truncate max-w-[220px]">
              {f.pickup_address} → {f.dropoff_address}
            </span>
            <span className="text-zinc-400">·</span>
            <span className="text-amber-200 font-semibold whitespace-nowrap">{fmtTime(f.requested_pickup_at)}</span>
            {f.assigned_driver_name ? (
              <span className="text-emerald-300 whitespace-nowrap">· {f.assigned_driver_name}</span>
            ) : (
              <span className="text-rose-300 whitespace-nowrap">· no driver</span>
            )}
          </div>
        ))}
        {fares.length > 5 && (
          <div className="text-[10px] text-zinc-500">+{fares.length - 5} more approaching…</div>
        )}
      </div>
    </div>
  );
}
